/* ============================================================
   Mybibblio — cart housekeeping (loads on every page; the cart
   drawer is global)

   Cascade-remove add-ons: when a personalised book is added with a
   paid add-on (dedication / hardback), both the book and the add-on
   carry a hidden `_kb_bundle` id (see mybibblio-addons.js). If the
   book is removed from the cart, its add-on(s) become "orphans" —
   a bundle id with no matching book line — and we remove them too.
   ============================================================ */
(function () {
  var ADDON_HANDLES = ['dedication-message', 'hardback-upgrade'];
  var busy = false;

  function cartSectionIds() {
    var ids = [];
    document.querySelectorAll('cart-items-component').forEach(function (c) {
      if (c.dataset && c.dataset.sectionId) ids.push(c.dataset.sectionId);
    });
    return ids.join(',');
  }

  function isAddon(item) { return ADDON_HANDLES.indexOf(item.handle) !== -1; }
  function bundleOf(item) { return item.properties && item.properties._kb_bundle; }

  function reconcile() {
    if (busy) return;
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart || !cart.items || !cart.items.length) return;

        // Bundle ids that still have a BOOK (non-add-on) line present.
        var liveBundles = {};
        cart.items.forEach(function (it) {
          var b = bundleOf(it);
          if (b && !isAddon(it)) liveBundles[b] = true;
        });

        // Orphaned add-ons: a bundle id whose book is gone.
        var orphans = cart.items.filter(function (it) {
          var b = bundleOf(it);
          return b && isAddon(it) && !liveBundles[b];
        });
        if (!orphans.length) { checkFormat(cart); return; }

        busy = true;
        var updates = {};
        orphans.forEach(function (it) { updates[it.key] = 0; });
        var ids = cartSectionIds();
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(ids ? { updates: updates, sections: ids } : { updates: updates })
        })
          .then(function (r) { return r.json(); })
          .then(function (cart2) {
            busy = false;
            document.dispatchEvent(new CustomEvent('cart:update', {
              bubbles: true,
              detail: { resource: cart2, sourceId: 'mybibblio-cart', data: { source: 'mybibblio-cart', sections: cart2.sections } }
            }));
            checkFormat(cart2);
          })
          .catch(function () { busy = false; });
      })
      .catch(function () {});
  }

  document.addEventListener('cart:update', function (e) {
    if (e && e.detail && e.detail.sourceId === 'mybibblio-cart') return; // ignore our own refresh
    reconcile();
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', reconcile);
  else reconcile();

  /* ============================================================
     Hardback / Format guard
     A book line carries `Format: Hardback`, and the hardback upgrade is a
     SEPARATE line. If the shopper removes that upgrade line, the book was
     still being sent to print as a hardback without being paid for (a real
     order slipped through this way). We deliberately do NOT intercept the
     theme's remove button — breaking removal would be far worse than the
     bug. Instead we detect the mismatch after the fact (this already runs
     on every cart:update) and ask the shopper what they meant.
     Closing the dialog resolves to paperback, which is the truthful state
     for what they have actually paid for.
     ============================================================ */
  var HARDBACK_HANDLE = 'hardback-upgrade';
  var HARDBACK_VARIANT = 57941402419532; // matches p_format block in templates/product.json
  var HARDBACK_PRICE = '£5.00';
  var dialogOpen = false;

  // Book lines that say Hardback but have no matching upgrade line.
  function mismatched(cart) {
    var items = (cart && cart.items) || [];
    var upgrades = items.filter(function (it) { return it.handle === HARDBACK_HANDLE; });
    return items.filter(function (it) {
      if (isAddon(it)) return false;
      var p = it.properties || {};
      if (p.Format !== 'Hardback') return false;
      var b = p._kb_bundle;
      // No bundle id (legacy line): only flag when there is no upgrade at all,
      // so we never nag someone whose cart is actually fine.
      if (!b) return upgrades.length === 0;
      return !upgrades.some(function (u) { return (u.properties || {})._kb_bundle === b; });
    });
  }

  function cartJson() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
  }

  function announce(cart2) {
    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: { resource: cart2, sourceId: 'mybibblio-cart', data: { source: 'mybibblio-cart', sections: cart2.sections } }
    }));
  }

  function changeLine(body) {
    var ids = cartSectionIds();
    if (ids) body.sections = ids;
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) { if (!r.ok) throw new Error('change-failed'); return r.json(); });
  }

  // Rewrite Format -> Paperback on every mismatched line, one at a time
  // (each write changes the line key, so we re-read between passes).
  function resolvePaperback(done) {
    var guard = 0;
    (function step() {
      if (++guard > 8) return done && done();
      cartJson().then(function (cart) {
        var bad = mismatched(cart);
        if (!bad.length) { announce(cart); return done && done(); }
        var line = bad[0];
        var props = {};
        Object.keys(line.properties || {}).forEach(function (k) { props[k] = line.properties[k]; });
        props.Format = 'Paperback';
        changeLine({ id: line.key, properties: props }).then(step).catch(function () { done && done(); });
      }).catch(function () { done && done(); });
    })();
  }

  // Put the hardback upgrade back, linked to the book(s) that need it.
  function resolveHardback(done) {
    cartJson().then(function (cart) {
      var bad = mismatched(cart);
      if (!bad.length) { announce(cart); return done && done(); }
      var bundle = (bad[0].properties || {})._kb_bundle;
      var group = bad.filter(function (l) { return (l.properties || {})._kb_bundle === bundle; });
      var qty = group.reduce(function (n, l) { return n + (l.quantity || 1); }, 0) || 1;
      var name = (bad[0].properties || {})['Child name'] || 'your book';

      function addUpgrade(bundleId) {
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: HARDBACK_VARIANT, quantity: qty, properties: { 'Add-on for': name, _kb_bundle: bundleId } }] })
        }).then(function (r) { if (!r.ok) throw new Error('add-failed'); return r.json(); });
      }

      var chain;
      if (bundle) {
        chain = addUpgrade(bundle);
      } else {
        // Legacy line with no bundle id: mint one and stamp it on the book first.
        var nb = newBundle();
        var line = bad[0];
        var props = {};
        Object.keys(line.properties || {}).forEach(function (k) { props[k] = line.properties[k]; });
        props._kb_bundle = nb;
        chain = changeLine({ id: line.key, properties: props }).then(function () { return addUpgrade(nb); });
      }

      chain
        .then(cartJson)
        .then(function (c2) { announce(c2); done && done(); })
        .catch(function () {
          // Could not restore -> fall back to the paid-for state.
          resolvePaperback(done);
        });
    }).catch(function () { done && done(); });
  }

  function closeDialog(el) {
    if (!el) return;
    el.classList.remove('is-open');
    setTimeout(function () { if (el && el.parentNode) el.parentNode.removeChild(el); }, 200);
    dialogOpen = false;
  }

  function esc(t) { return String(t).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }
  // "Aanya" or, on a two-child book, "Aanya & Zayd"
  function bookNames(line) {
    var pr = (line && line.properties) || {};
    var n = pr['Child name'] || '';
    return n && pr['Child 2 name'] ? n + ' & ' + pr['Child 2 name'] : n;
  }

  function showFormatDialog(name) {
    if (dialogOpen || document.querySelector('[data-kb-fmtq]')) return;
    dialogOpen = true;
    var who = name ? ('<strong>' + esc(name) + '</strong>’s book') : 'Your book';
    var el = document.createElement('div');
    el.className = 'kb-fmtq';
    el.setAttribute('data-kb-fmtq', '');
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    el.setAttribute('aria-label', 'Hardback upgrade removed');
    el.innerHTML =
      '<div class="kb-fmtq__backdrop" data-kb-fmtq-paper></div>' +
      '<div class="kb-fmtq__box">' +
        '<span class="kb-fmtq__ico" aria-hidden="true">📖</span>' +
        '<h2 class="kb-fmtq__title">Keep it a hardback?</h2>' +
        '<p class="kb-fmtq__text">You removed the hardback upgrade, so ' + who +
          ' will be printed and sent as a <strong>paperback</strong>.</p>' +
        '<div class="kb-fmtq__acts">' +
          '<button type="button" class="kb-fmtq__keep" data-kb-fmtq-keep>' +
            'Keep my book as a hardback <span class="kb-fmtq__price">' + HARDBACK_PRICE + '</span>' +
          '</button>' +
          '<button type="button" class="kb-fmtq__switch" data-kb-fmtq-paper>No thanks, paperback is fine</button>' +
        '</div>' +
        '<p class="kb-fmtq__note">Hardbacks are sewn and case-bound — the ones that survive bedtime after bedtime.</p>' +
      '</div>';
    document.body.appendChild(el);
    requestAnimationFrame(function () { el.classList.add('is-open'); });

    var keepBtn = el.querySelector('[data-kb-fmtq-keep]');
    function lock() {
      el.classList.add('is-busy');
      Array.prototype.forEach.call(el.querySelectorAll('button'), function (b) { b.disabled = true; });
    }
    keepBtn.addEventListener('click', function () { lock(); resolveHardback(function () { closeDialog(el); }); });
    Array.prototype.forEach.call(el.querySelectorAll('[data-kb-fmtq-paper]'), function (b) {
      b.addEventListener('click', function () { lock(); resolvePaperback(function () { closeDialog(el); }); });
    });
    el.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { lock(); resolvePaperback(function () { closeDialog(el); }); }
    });
    keepBtn.focus();
  }

  function checkFormat(cart) {
    try {
      var bad = mismatched(cart);
      if (!bad.length) return;
      showFormatDialog(bookNames(bad[0]));
    } catch (err) { /* never let this break the cart */ }
  }

  /* ---- Inline cart editing: name / who / art style --------------------
     Each personalised book line has an "Edit details" button + panel. On
     Save we build the updated line (new variant + name/pronoun), ADD it,
     then REMOVE the old line (+ its add-ons), re-linking add-ons if any. */
  function newBundle() { return 'kb-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8); }

  function ceditMsg(panel, text, isErr) {
    var m = panel.querySelector('[data-kb-cedit-msg]');
    if (!m) return;
    if (!text) { m.hidden = true; m.textContent = ''; return; }
    m.hidden = false; m.textContent = text; m.className = 'kb-cedit__msg' + (isErr ? ' is-err' : '');
  }

  document.addEventListener('click', function (e) {
    var t = e.target; if (!t || !t.closest) return;

    var toggle = t.closest('[data-kb-edit-toggle]');
    if (toggle) {
      var wrap = toggle.closest('.cart-items__details') || toggle.parentNode;
      var panel = wrap && wrap.querySelector('[data-kb-cedit]');
      if (panel) { panel.hidden = !panel.hidden; cedifocus(panel); }
      return;
    }
    var opt = t.closest('[data-kb-cedit-pronoun], [data-kb-cedit-pronoun2], [data-kb-cedit-style]');
    if (opt) {
      opt.parentNode.querySelectorAll('.kb-cedit__opt').forEach(function (b) { b.classList.remove('is-on'); });
      opt.classList.add('is-on');
      return;
    }
    var cancel = t.closest('[data-kb-cedit-cancel]');
    if (cancel) { var pc = cancel.closest('[data-kb-cedit]'); if (pc) pc.hidden = true; return; }
    var save = t.closest('[data-kb-cedit-save]');
    if (save) { saveEdit(save.closest('[data-kb-cedit]'), save); return; }
  });

  function cedifocus(panel) {
    var i = panel && !panel.hidden && panel.querySelector('[data-kb-cedit-name]');
    if (i) { try { i.focus(); } catch (e) {} }
  }

  function saveEdit(panel, saveBtn) {
    if (!panel || !saveBtn) return;
    var key = panel.getAttribute('data-key');
    var nameEl = panel.querySelector('[data-kb-cedit-name]');
    var name = nameEl ? nameEl.value.trim() : '';
    var pronEl = panel.querySelector('[data-kb-cedit-pronoun].is-on');
    var pronoun = pronEl ? pronEl.getAttribute('data-kb-cedit-pronoun') : '';
    var styleEl = panel.querySelector('[data-kb-cedit-style].is-on');
    var newVid = styleEl ? Number(styleEl.getAttribute('data-vid')) : 0;
    // second child (only present on two-child books)
    var name2El = panel.querySelector('[data-kb-cedit-name2]');
    var name2 = name2El ? name2El.value.trim() : '';
    var pron2El = panel.querySelector('[data-kb-cedit-pronoun2].is-on');
    var pronoun2 = pron2El ? pron2El.getAttribute('data-kb-cedit-pronoun2') : '';

    if (!name) { ceditMsg(panel, 'Please enter a name.', true); return; }
    if (!pronoun) { ceditMsg(panel, 'Please choose Boy or Girl.', true); return; }
    if (name2El && !name2) { ceditMsg(panel, 'Please enter the second child’s name.', true); return; }
    if (name2El && !pronoun2) { ceditMsg(panel, 'Please choose Boy or Girl for the second child.', true); return; }
    if (!newVid) { ceditMsg(panel, 'Please choose an art style.', true); return; }

    saveBtn.disabled = true; ceditMsg(panel, 'Saving…', false);

    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var oldLine = (cart.items || []).filter(function (it) { return it.key === key; })[0];
        if (!oldLine) throw new Error('line-gone');

        var props = oldLine.properties || {};
        // Nothing changed -> just close (avoids a merge-then-delete that could drop the line).
        var same2 = !name2El || (name2 === (props['Child 2 name'] || '') && pronoun2 === (props['Child 2 pronoun'] || ''));
        if (same2 && name === (props['Child name'] || '') && pronoun === (props['Pronoun'] || '') && newVid === oldLine.variant_id) {
          panel.hidden = true; saveBtn.disabled = false; ceditMsg(panel, '', false); return;
        }

        var oldBundle = props._kb_bundle;
        var addonLines = oldBundle ? (cart.items || []).filter(function (it) { return isAddon(it) && it.properties && it.properties._kb_bundle === oldBundle; }) : [];

        var newProps = {};
        Object.keys(props).forEach(function (k) { newProps[k] = props[k]; });
        newProps['Child name'] = name;
        newProps['Pronoun'] = pronoun;
        if (name2El) { newProps['Child 2 name'] = name2; newProps['Child 2 pronoun'] = pronoun2; }
        var addonFor = name2El ? name + ' & ' + name2 : name;
        var nb = null;
        if (addonLines.length) { nb = newBundle(); newProps['_kb_bundle'] = nb; }
        else if (newProps['_kb_bundle']) { delete newProps['_kb_bundle']; }

        var items = [{ id: newVid, quantity: oldLine.quantity, properties: newProps }];
        addonLines.forEach(function (a) {
          var p = {}; Object.keys(a.properties || {}).forEach(function (k) { p[k] = a.properties[k]; });
          p['Add-on for'] = addonFor; if (nb) p['_kb_bundle'] = nb;
          items.push({ id: a.variant_id, quantity: a.quantity, properties: p });
        });

        return fetch('/cart/add.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: items })
        })
          .then(function (r) { if (!r.ok) throw new Error('add-failed'); return r.json(); })
          .then(function () {
            var updates = {}; updates[oldLine.key] = 0;
            addonLines.forEach(function (a) { updates[a.key] = 0; });
            var ids = cartSectionIds();
            return fetch('/cart/update.js', {
              method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify(ids ? { updates: updates, sections: ids } : { updates: updates })
            }).then(function (r) { return r.json(); });
          })
          .then(function (cart2) {
            document.dispatchEvent(new CustomEvent('cart:update', {
              bubbles: true,
              detail: { resource: cart2, sourceId: 'mybibblio-cart', data: { source: 'mybibblio-cart', sections: cart2.sections } }
            }));
          });
      })
      .catch(function () {
        saveBtn.disabled = false;
        ceditMsg(panel, 'Sorry — that didn’t save. Please try again.', true);
      });
  }
})();

/* ============================================================
   Photo previews in the cart — private by design.
   Customer photos are stored privately, so the cart cannot load
   them. When a photo is uploaded on the product page,
   mybibblio-addons.js keeps a small preview in THIS browser only
   (localStorage, keyed by the stored photo's address). The cart
   shows that preview so the shopper can check they picked the
   right photo; on any other device it shows the "✓ Added" tag.
   Previews never leave the device and expire after 7 days.
   ============================================================ */
(function () {
  var PREFIX = 'kb-thumb:';
  var MAX_AGE = 7 * 24 * 3600 * 1000;
  var IMG_RE = /^data:image\/(jpeg|png|webp);base64,/;
  var misses = {};

  function store() { try { return window.localStorage; } catch (e) { return null; } }

  // drop expired previews; `keep` (optional) = addresses still in the cart;
  // `force` drops the oldest half (used when storage is full)
  function prune(opts) {
    var ls = store(); if (!ls) return;
    opts = opts || {};
    try {
      var items = [], now = Date.now();
      for (var i = 0; i < ls.length; i++) {
        var k = ls.key(i);
        if (!k || k.indexOf(PREFIX) !== 0) continue;
        var t = 0; try { t = JSON.parse(ls.getItem(k)).t || 0; } catch (e) {}
        items.push({ k: k, t: t });
      }
      items.sort(function (a, b) { return a.t - b.t; });
      items.forEach(function (it, idx) {
        var url = it.k.slice(PREFIX.length);
        var drop = now - it.t > MAX_AGE ||
          (opts.force && idx < Math.ceil(items.length / 2)) ||
          (opts.keep && opts.keep.indexOf(url) === -1 && now - it.t > 3600 * 1000);
        if (drop) ls.removeItem(it.k);
      });
    } catch (e) {}
  }

  function save(url, data) {
    var ls = store();
    if (!ls || !url || !IMG_RE.test(data || '')) return;
    var v = JSON.stringify({ d: data, t: Date.now() });
    try { ls.setItem(PREFIX + url, v); }
    catch (e) { prune({ force: true }); try { ls.setItem(PREFIX + url, v); } catch (e2) {} }
    delete misses[url];
  }

  function get(url) {
    var ls = store();
    if (!ls || !url) return null;
    try {
      var o = JSON.parse(ls.getItem(PREFIX + url) || 'null');
      if (!o || Date.now() - o.t > MAX_AGE || !IMG_RE.test(o.d || '')) return null;
      return o.d;
    } catch (e) { return null; }
  }

  window.kbThumbs = { save: save, get: get };

  function apply() {
    document.querySelectorAll('[data-kb-thumb]').forEach(function (el) {
      if (el.querySelector('.kb-cart-thumb')) return;
      var url = el.getAttribute('data-kb-thumb');
      if (misses[url]) return;
      var d = get(url);
      if (!d) { misses[url] = true; return; }      // keeps the "✓ Added" tag
      var alt = el.getAttribute('data-kb-thumb-alt') || 'Your uploaded photo';
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'kb-cart-thumb';
      btn.setAttribute('aria-label', 'View ' + alt.charAt(0).toLowerCase() + alt.slice(1) + ' larger');
      var img = document.createElement('img');
      img.className = 'kb-cart-photo';
      img.alt = alt; img.width = 52; img.height = 52;
      img.src = d;
      btn.appendChild(img);
      el.classList.remove('kb-cart-photo-link--private');
      el.appendChild(btn);
    });
  }

  // enlarged view; opened inside the cart drawer <dialog> when there, since a
  // modal dialog sits above everything else on the page
  var box = null;
  function closeBox() { if (box) box.hidden = true; }
  function openBox(src, alt, host) {
    if (!box) {
      box = document.createElement('div');
      box.className = 'kb-cart-lightbox';
      box.setAttribute('role', 'dialog');
      box.setAttribute('aria-modal', 'true');
      box.setAttribute('aria-label', 'Your uploaded photo');
      box.innerHTML = '<button type="button" class="kb-cart-lightbox__close" aria-label="Close">×</button>' +
        '<figure class="kb-cart-lightbox__fig"><img alt=""><figcaption>Only shown on this device &mdash; your photo is stored privately.</figcaption></figure>';
      box.addEventListener('click', function (e) {
        if (e.target === box || (e.target.closest && e.target.closest('.kb-cart-lightbox__close'))) {
          e.preventDefault(); e.stopPropagation(); closeBox();
        }
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && box && !box.hidden) { e.stopPropagation(); closeBox(); }
      }, true);
    }
    if (box.parentNode !== host) host.appendChild(box);
    var im = box.querySelector('img');
    im.src = src; im.alt = alt;
    box.hidden = false;
    try { box.querySelector('.kb-cart-lightbox__close').focus({ preventScroll: true }); } catch (e) {}
  }

  document.addEventListener('click', function (e) {
    var b = e.target.closest && e.target.closest('.kb-cart-thumb');
    if (!b) return;
    e.preventDefault(); e.stopPropagation();
    var img = b.querySelector('img');
    openBox(img.src, img.alt, b.closest('dialog') || document.body);
  });

  // the cart drawer and cart page re-render on every change, so re-apply
  var queued = false;
  function schedule() {
    if (queued) return;
    queued = true;
    setTimeout(function () { queued = false; try { apply(); } catch (e) {} }, 60);
  }

  function start() {
    prune();
    try { apply(); } catch (e) {}
    // on the full cart page, previews for photos no longer in the cart are deleted
    if (document.querySelector('main[data-template="cart"]')) {
      var keep = [];
      document.querySelectorAll('[data-kb-thumb]').forEach(function (el) { keep.push(el.getAttribute('data-kb-thumb')); });
      prune({ keep: keep });
    }
    try { new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true }); } catch (e) {}
    document.addEventListener('cart:update', schedule);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
